import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Eye, Bell, BellOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import api from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { formatGs, ORDER_STATUS } from '@/utils/helpers';
import { useOrderAlerts, requestNotificationPermission, getNotificationStatus } from '@/hooks/useOrderAlerts';

const STATUSES = ['', 'pendiente', 'cocina', 'en_camino', 'entregado', 'cancelado', 'rechazado'];

function NotificationBanner() {
  const [status, setStatus] = useState(getNotificationStatus);
  if (status === 'granted' || status === 'unsupported') return null;

  const ask = async () => {
    const result = await requestNotificationPermission();
    setStatus(result);
  };

  return (
    <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 mb-4" data-testid="notification-banner">
      {status === 'denied' ? (
        <>
          <BellOff size={16} className="text-amber-400 shrink-0" />
          <p className="text-xs text-zinc-300 flex-1">Las notificaciones estan bloqueadas. Habilitarlas desde la configuracion del navegador para recibir alertas de nuevos pedidos.</p>
        </>
      ) : (
        <>
          <Bell size={16} className="text-amber-400 shrink-0" />
          <p className="text-xs text-zinc-300 flex-1">Activa las notificaciones para recibir alertas cuando llegue un nuevo pedido.</p>
          <button onClick={ask} className="text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30 px-3 py-1.5 rounded-lg hover:bg-amber-500/30 transition-colors whitespace-nowrap" data-testid="notification-enable-btn">
            Activar notificaciones
          </button>
        </>
      )}
    </div>
  );
}

export default function OrdersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.roles?.includes('admin_global');
  const [orders, setOrders] = useState([]);
  const [branches, setBranches] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ status: '', branch_id: '' });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Polling alerts for new orders
  useOrderAlerts(navigate);

  useEffect(() => {
    if (isAdmin) api.get('/sucursales').then(r => setBranches(r.data.sucursales)).catch(() => {});
  }, [isAdmin]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (filters.status) params.set('status', filters.status);
      if (filters.branch_id) params.set('branch_id', filters.branch_id);
      const { data } = await api.get(`/pedidos?${params}`);
      setOrders(data.orders || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch { setOrders([]); }
    setLoading(false);
  }, [page, filters]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filtered = search
    ? orders.filter(o => o.id.toString().includes(search) || o.customer_name?.toLowerCase().includes(search.toLowerCase()))
    : orders;

  return (
    <div data-testid="admin-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold">Pedidos</h2>
          <p className="text-sm text-zinc-500">{total} pedidos en total</p>
        </div>
      </div>

      <NotificationBanner />

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por # o cliente..." className="bg-zinc-900 border-zinc-800 h-9 text-sm pl-9" data-testid="orders-search" />
        </div>
        <select value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 h-9 text-sm text-zinc-300 outline-none" data-testid="orders-status-filter">
          <option value="">Todos los estados</option>
          {STATUSES.filter(Boolean).map(s => <option key={s} value={s}>{ORDER_STATUS[s]?.label || s}</option>)}
        </select>
        {isAdmin && (
          <select value={filters.branch_id} onChange={e => { setFilters(f => ({ ...f, branch_id: e.target.value })); setPage(1); }} className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 h-9 text-sm text-zinc-300 outline-none" data-testid="orders-branch-filter">
            <option value="">Todas las sucursales</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {/* Table */}
      <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800/50 text-zinc-500 text-xs uppercase tracking-wider">
                <th className="text-left p-3 font-medium">#</th>
                <th className="text-left p-3 font-medium">Cliente</th>
                <th className="text-left p-3 font-medium">Sucursal</th>
                <th className="text-left p-3 font-medium">Total</th>
                <th className="text-left p-3 font-medium">Estado</th>
                <th className="text-left p-3 font-medium">Fecha</th>
                <th className="text-right p-3 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-zinc-800/30"><td colSpan={7} className="p-3"><div className="h-5 bg-zinc-800 rounded animate-pulse" /></td></tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-zinc-500">No hay pedidos.</td></tr>
              ) : (
                filtered.map(o => {
                  const st = ORDER_STATUS[o.status] || { label: o.status, color: 'bg-zinc-500/20 text-zinc-400' };
                  return (
                    <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-zinc-800/30 hover:bg-zinc-800/20 transition-colors">
                      <td className="p-3 font-mono font-medium text-[#E84C1C]">#{o.id}</td>
                      <td className="p-3 text-zinc-300">{o.customer_name}</td>
                      <td className="p-3 text-zinc-400 text-xs">{o.branch_name}</td>
                      <td className="p-3 font-medium">{formatGs(o.total)}</td>
                      <td className="p-3"><span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span></td>
                      <td className="p-3 text-zinc-500 text-xs">{new Date(o.created_at).toLocaleString('es-PY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                      <td className="p-3 text-right">
                        <Link to={`/admin/pedidos/${o.id}`} data-testid={`order-view-${o.id}`} className="inline-flex items-center gap-1 text-xs text-[#E84C1C] hover:text-[#ff6b3d] transition-colors">
                          <Eye size={14} /> Ver
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800/50">
            <span className="text-xs text-zinc-500">Pagina {page} de {pages}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 rounded-lg hover:bg-zinc-800 disabled:opacity-30 transition-colors"><ChevronLeft size={16} /></button>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} className="p-1.5 rounded-lg hover:bg-zinc-800 disabled:opacity-30 transition-colors"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
